import React, { useState, useEffect } from 'react';
import styles from './TradingAccountModal.module.css';

const API_BASE_URL = 'https://final-project-5ar7.onrender.com';

function TradingAccountModal({ isOpen, onClose, onRequestSubmitted, userId, userName, userBalance }) {
  const [formData, setFormData] = useState({
    accountNo: '',
    mainPassword: '',
    investorPassword: '',
    group: 'STANDARD',
    algoTradingType: '',
    leverage: '100',
    server: 'StoneForex Server'
  });
  const [loading, setLoading] = useState(false);
  const [accountError, setAccountError] = useState('');

  // Debug userId when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('🔍 Modal opened - userId:', userId);
      console.log('🔍 Modal opened - userName:', userName);
      
      if (!userId) {
        console.error('❌ No userId provided to modal');
      }
    }
  }, [isOpen, userId, userName]);

  // Main group options
  const groupOptions = [
    { label: 'STANDARD', value: 'STANDARD' },
    { label: 'VIP', value: 'VIP' },
    { label: 'PREMIUM', value: 'PREMIUM' },
    { label: 'GOLD', value: 'GOLD' },
    { label: 'PRO', value: 'PRO' },
    { label: 'ALGO TRADING', value: 'ALGO_TRADING' }
  ];

  // Algo Trading sub-types
  const algoTradingTypes = [
    { label: 'PREMIUM', value: 'PREMIUM' },
    { label: 'GOLD', value: 'GOLD' },
    { label: 'PRO', value: 'PRO' },
    { label: 'VIP', value: 'VIP' }
  ];

  // Leverage options
  const leverageOptions = [1, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000];

  // Server options
  const serverOptions = [
    { label: 'StoneForex Server', value: 'StoneForex Server' }
  ];

  // Validate account number
  const validateAccountNumber = (value) => {
    if (!value) return 'Account number is required';
    if (!/^\d{4}$/.test(value)) return 'Must be exactly 4 digits';
    const num = parseInt(value);
    if (num < 1000 || num > 9999) return 'Must be between 1000 and 9999';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'accountNo') {
      if (/^\d{0,4}$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setAccountError(validateAccountNumber(value));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'group' && value !== 'ALGO_TRADING') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        algoTradingType: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userId) {
      alert('❌ Session expired. Please refresh and login again.');
      return;
    }

    const accountValidation = validateAccountNumber(formData.accountNo);
    if (accountValidation) {
      alert(`❌ ${accountValidation}`);
      return;
    }

    if (!formData.mainPassword.trim()) {
      alert('❌ Please enter a main password for your trading account.');
      return;
    }

    if (!formData.investorPassword.trim()) {
      alert('❌ Please enter an investor password for your trading account.');
      return;
    }

    if (formData.group === 'ALGO_TRADING' && !formData.algoTradingType) {
      alert('❌ Please select an Algo Trading type.');
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('🚀 Creating trading account for userId:', userId);
      
      let finalGroup = formData.group;
      if (formData.group === 'ALGO_TRADING' && formData.algoTradingType) {
        finalGroup = `ALGO_${formData.algoTradingType}`;
      }
      
      const requestBody = {
        customAccountId: formData.accountNo,
        mainPassword: formData.mainPassword,
        investorPassword: formData.investorPassword,
        group: finalGroup,
        leverage: parseInt(formData.leverage),
        server: formData.server
      };

      console.log('📤 Request body:', requestBody);
      
      const response = await fetch(`${API_BASE_URL}/api/admin/public/create-trading-account/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      console.log('📥 API Response:', result);
      
      if (response.ok) {
        alert(`✅ Trading Account Created Successfully!\n\n🎯 Account Details:\n• Account Number: ${formData.accountNo}\n• User: ${userName}\n• Server: ${formData.server}\n• Group: ${finalGroup}\n\n💰 Your account is now ready for trading.\n📊 Account will appear in your dashboard immediately.`);
        
        if (typeof onRequestSubmitted === 'function') {
          onRequestSubmitted();
        }
        
        onClose();
        
        setFormData({
          accountNo: '',
          mainPassword: '',
          investorPassword: '',
          group: 'STANDARD',
          algoTradingType: '',
          leverage: '100',
          server: 'StoneForex Server'
        });
        setAccountError('');
      } else {
        alert(`❌ Failed to create trading account:\n${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error creating trading account:', error);
      alert('❌ Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isFormValid = userId && 
                     !accountError && 
                     formData.accountNo.length === 4 && 
                     formData.mainPassword.trim() && 
                     formData.investorPassword.trim() &&
                     (formData.group !== 'ALGO_TRADING' || formData.algoTradingType);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerText}>
            <h2>Create Trading Account</h2>
            <span className={styles.userInfo}>for {userName || 'User'}</span>
          </div>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        
        {!userId && (
          <div className={styles.warningAlert}>
            ⚠️ Warning: Session expired. Please refresh and login again.
          </div>
        )}
        
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Trading Account Number * (4 digits: 1000-9999)</label>
              <input
                type="text"
                name="accountNo"
                value={formData.accountNo}
                onChange={handleChange}
                placeholder="e.g., 2025"
                maxLength="4"
                required
                style={{
                  borderColor: accountError ? '#ef4444' : '#D1D5DB'
                }}
              />
              {accountError && (
                <small className={styles.errorText}>
                  {accountError}
                </small>
              )}
              <small className={styles.helperText}>
                This is separate from your main login credentials
              </small>
            </div>
            <div className={styles.formGroup}>
              <label>Trading Server</label>
              <select name="server" value={formData.server} onChange={handleChange}>
                {serverOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Trading Main Password *</label>
              <input
                type="password"
                name="mainPassword"
                value={formData.mainPassword}
                onChange={handleChange}
                placeholder="Enter trading password"
                required
              />
              <small className={styles.helperText}>
                For full trading access (separate from login password)
              </small>
            </div>
            <div className={styles.formGroup}>
              <label>Investor Password *</label>
              <input
                type="password"
                name="investorPassword"
                value={formData.investorPassword}
                onChange={handleChange}
                placeholder="Enter investor password"
                required
              />
              <small className={styles.helperText}>
                For read-only access to trading account
              </small>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Account Group</label>
              <select name="group" value={formData.group} onChange={handleChange}>
                {groupOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Leverage</label>
              <select name="leverage" value={formData.leverage} onChange={handleChange}>
                {leverageOptions.map(leverage => (
                  <option key={leverage} value={leverage}>
                    1:{leverage}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {formData.group === 'ALGO_TRADING' && (
            <div className={styles.formGroup}>
              <label>Algo Trading Type *</label>
              <select
                name="algoTradingType"
                value={formData.algoTradingType}
                onChange={handleChange}
                required
              >
                <option value="">Select Algo Trading Type</option>
                {algoTradingTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ✅ NO ADMIN MENTIONS - USER THINKS IT'S AUTOMATIC */}
          <div className={styles.balanceNotice}>
            <p>💰 <strong>Initial Balance:</strong> $0.00 (Ready to deposit and start trading)</p>
            <p>📋 <strong>Note:</strong> This trading account is linked to your main account ({userName}) but uses separate credentials for trading platform access.</p>
          </div>

          <div className={styles.formActions}>
            <button type="button" onClick={onClose} className={styles.cancelButton}>
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={loading || !isFormValid}
              className={styles.createButton}
            >
              {loading ? 'Creating Trading Account...' : 'Create Trading Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TradingAccountModal;
